import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@arco-design/web-react';
import { IconDown, IconUp } from '@arco-design/web-react/icon';

import styles from './index.module.less';

/** 为 Arco 行内详情字段保留标签宽度，并约束长文本在剩余空间内省略。 */
export const detailTextDescriptionsClassName = styles.descriptions;

/** 判断详情字段是否为描述字段。 */
export const isDetailDescriptionField = (key: string) => /(^|\.)description(?:__\d+)?$/i.test(key);

/** 判断详情字段是否为需要单行展示的 URL 或地址字段。 */
export const isDetailAddressField = (key: string) => /(url|address)(?:__\d+)?$/i.test(key);

/** 详情页长描述，默认三行并支持原位展开或收起。 */
export function ExpandableDescription({ value, rows = 3 }: { value?: string | null; rows?: number }) {
  const { t } = useTranslation();

  return (
    <Typography.Ellipsis
      className={styles.description}
      rows={rows}
      expandable={{ single: true }}
      expandRender={(expanded) => (
        <span className={styles.descriptionAction}>
          {t(expanded ? 'components:collapse' : 'components:expand')}
          {expanded ? <IconUp /> : <IconDown />}
        </span>
      )}
      style={{ whiteSpace: 'pre-wrap' }}
    >
      {value || '-'}
    </Typography.Ellipsis>
  );
}

/** 详情页普通文本，保持单行并在悬浮时展示完整内容。 */
export function EllipsisText({ value }: { value?: string | null }) {
  const displayValue = value || '-';
  return (
    <Typography.Ellipsis className={styles.text} rows={1} expandable={false} showTooltip={{ content: displayValue }}>
      {displayValue}
    </Typography.Ellipsis>
  );
}

/** 保留地址文本组件的既有导出，展示规则与普通文本一致。 */
export const EllipsisAddress = EllipsisText;

/** 按字段语义统一渲染详情长文本。 */
export function renderDetailTextValue(key: string, value: ReactNode): ReactNode {
  if (typeof value !== 'string') return value;
  if (isDetailDescriptionField(key)) return <ExpandableDescription value={value} />;
  return <EllipsisText value={value} />;
}
